import { useEffect, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Accordion, Alert, Anchor, Badge, Box, Button, Checkbox, Divider, Group, List, Loader, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ShieldCheck } from 'lucide-react';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { SurfaceCard } from '@/design/SurfaceCard';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { useAuth } from '@/features/auth/AuthContext';
import { LegalAcceptanceViewModel } from './LegalAcceptanceViewModel';

const DATA_REGION_LABEL = 'US East (Ohio) — us-east-2';

interface Subprocessor {
    name: string;
    purpose: string;
}

const SUBPROCESSORS: Subprocessor[] = [
    { name: 'Amazon Web Services', purpose: `Cloud hosting, object storage, and email delivery (${DATA_REGION_LABEL}).` },
    { name: 'Anthropic (via Amazon Bedrock)', purpose: 'AI usage classification and summarization of ingested activity.' },
    { name: 'Your connected AI providers', purpose: 'OpenAI, Google, Microsoft, and others you connect — the source of the usage data you ingest.' },
];

/** Fuller body copy for each document, keyed by the document type the API returns. */
const DOCUMENT_BODY: Record<string, string[]> = {
    tos: [
        'These Terms of Service govern your organization’s access to and use of the Ultra Trace platform. By accepting, you confirm you are authorized to bind your organization.',
        'You agree to use the platform only for its intended purpose — analyzing your organization’s own AI-tool usage — and not to attempt to access other tenants’ data, disrupt the service, or reverse-engineer it.',
        'The service is provided on a subscription basis per your plan. We may update features and these terms; material changes re-prompt acceptance. Either party may terminate per the subscription agreement; on termination your tenant is offboarded and its data deleted.',
    ],
    dpa: [
        'This Data Processing Agreement describes how Ultra Trace processes your organization’s data. You are the data controller; Ultra Trace is a processor acting only on your documented instructions.',
        'We process usage telemetry, identity/directory records, and license data solely to provide the analytics you configure. Data is hosted in the region shown below. You control retention windows and content-redaction options in Settings → Data & Retention.',
        'We maintain tenant isolation (row-level security and tenant-scoped storage), encryption in transit and at rest, and access controls. On offboarding we delete your tenant’s data, including connected-provider credentials, across all services.',
        'We engage the subprocessors listed below to deliver the service. We will give notice before adding a subprocessor. Data crosses a boundary only as needed for these subprocessors to operate, under this agreement.',
    ],
};

const PRIVACY_BODY = [
    'Our Privacy Policy explains what personal data the platform handles and why. The platform is a workplace analytics tool: it processes work-account identifiers (e.g. email addresses) and AI-tool usage metadata belonging to your organization’s users.',
    'You can exclude prompt/response content, file contents, raw IP addresses, and end-user PII at ingestion, and set retention windows, in Settings → Data & Retention. Individuals’ access/erasure requests are handled by your organization as controller, with our support as processor.',
];

/**
 * First-run legal-acceptance gate (GL-42). Publishes the Terms of Service, Data
 * Processing Agreement, privacy summary, and subprocessor list, and records the
 * tenant admin's acceptance on behalf of the organization. Rendered inside the
 * minimal `OnboardingLayout`; the router keeps an un-accepted admin here until
 * they accept (there is no skip — unlike the onboarding wizard).
 */
export default function LegalAcceptancePage(): ReactElement {
    const vm = useViewModel(LegalAcceptanceViewModel);
    const { reloadLegal } = useAuth();
    const navigate = useNavigate();
    const [agreed, setAgreed] = useState(false);

    useEffect(() => { void vm.load(); }, [vm]);

    const onAccept = async (): Promise<void> => {
        const ok = await vm.accept();
        if (!ok) {
            notifications.show({ color: 'red', message: vm.error ?? 'Failed to record acceptance' });
            return;
        }
        await reloadLegal();
        navigate('/', { replace: true });
    };

    if (vm.loading && !vm.status) {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        );
    }

    const documents = vm.status?.documents ?? [];

    return (
        <Stack gap="lg">
            <PageHeaderComponent
                eyebrow="Before you get started"
                title="Review & accept our terms"
                subtitle="Your organization needs to accept these agreements before using Ultra Trace. You’re accepting on behalf of your organization."
                leading={<ShieldCheck size={22} />}
            />

            <SurfaceCard $padding="lg" $static>
                <Stack gap="md">
                    <Accordion multiple variant="separated" defaultValue={documents.map((d) => d.type)}>
                        {documents.map((doc) => (
                            <Accordion.Item key={doc.type} value={doc.type}>
                                <Accordion.Control>
                                    <Group gap="xs">
                                        <Text fw={600}>{doc.title}</Text>
                                        {doc.accepted && <Badge size="xs" color="teal" variant="light">Accepted</Badge>}
                                    </Group>
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Stack gap="sm">
                                        <Text size="sm" c="dimmed">{doc.summary}</Text>
                                        {(DOCUMENT_BODY[doc.type] ?? []).map((para, i) => (
                                            <Text key={i} size="sm">{para}</Text>
                                        ))}
                                    </Stack>
                                </Accordion.Panel>
                            </Accordion.Item>
                        ))}

                        <Accordion.Item value="privacy">
                            <Accordion.Control><Text fw={600}>Privacy Policy</Text></Accordion.Control>
                            <Accordion.Panel>
                                <Stack gap="sm">
                                    {PRIVACY_BODY.map((para, i) => (
                                        <Text key={i} size="sm">{para}</Text>
                                    ))}
                                </Stack>
                            </Accordion.Panel>
                        </Accordion.Item>

                        <Accordion.Item value="subprocessors">
                            <Accordion.Control><Text fw={600}>Subprocessors</Text></Accordion.Control>
                            <Accordion.Panel>
                                <List spacing="xs" size="sm">
                                    {SUBPROCESSORS.map((sp) => (
                                        <List.Item key={sp.name}>
                                            <Text component="span" fw={600}>{sp.name}</Text>
                                            <Text component="span" c="dimmed"> — {sp.purpose}</Text>
                                        </List.Item>
                                    ))}
                                </List>
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>

                    {vm.error && <Alert color="red" variant="light">{vm.error}</Alert>}

                    <Divider />

                    <Checkbox
                        checked={agreed}
                        onChange={(e) => setAgreed(e.currentTarget.checked)}
                        label={
                            <Text size="sm">
                                I have read and, on behalf of my organization, agree to the Terms of Service and Data Processing Agreement.
                            </Text>
                        }
                    />

                    <Group justify="space-between" align="center">
                        <Text size="xs" c="dimmed">
                            Version {vm.status?.currentVersion ?? '—'}. Questions? Contact{' '}
                            <Anchor href="mailto:privacy@ultratrace.com" size="xs">privacy@ultratrace.com</Anchor>.
                        </Text>
                        <Box>
                            <Button onClick={() => void onAccept()} disabled={!agreed} loading={vm.submitting}>
                                Accept & continue
                            </Button>
                        </Box>
                    </Group>
                </Stack>
            </SurfaceCard>
        </Stack>
    );
}
